import { useEffect, useState, useRef } from 'react';
import { DataTypesEnum, ObservableLevelEnumValue } from '../../data/enums';
import { observationService } from '../../services/observationService';
import { useObservationMutations } from '../../hooks/useObservationMutations';
import { Label } from './Label';
import { Tooltip } from './Tooltip';
import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';
import { cacheInvalidationService } from '../../services/cacheInvalidationService';

type Option = {
  value: string;
  label: string;
};

interface RadioButtonGroupFieldProps {
  title?: string;
  onChange?: (value: string) => void;
  onDefaultLoad?: (value?: string) => void;
  alwaysRunOnDefaultLoad?: boolean;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  tooltipKey?: string;
  options: Option[];
  divider?: boolean;
  defaultValue?: string;
  saveDefaultValueOnLoad?: boolean;
}

const RadioButtonGroupField = ({
  title,
  tooltipKey,
  inspectionId,
  inspectionCategoryTypeId,
  dataLevel,
  entityId,
  entityExternalId,
  path,
  divider,
  onChange,
  onDefaultLoad,
  alwaysRunOnDefaultLoad,
  options,
  defaultValue,
  saveDefaultValueOnLoad = false,
}: RadioButtonGroupFieldProps) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const { upsertObservationAsync } = useObservationMutations();

  // Track if default loading has been executed to prevent repeated database calls
  const hasLoadedDefaultRef = useRef(false);
  const observationCacheRef = useRef<Map<string, string>>(new Map());

  const handleChange = async (value: string) => {
    // Update UI immediately for responsive feedback
    setSelectedValue(value);

    try {
      await upsertObservationAsync({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId: entityExternalId,
        inspectionCategoryId: inspectionCategoryTypeId,
        inspectionId: inspectionId,
        path: path,
        value: value,
      });

      // Call onChange after successful database update
      onChange?.(value);
    } catch (error) {
      // Revert UI state if database update fails
      console.error('Failed to update observation:', error);
      // Note: we don't revert setSelectedValue here as the UI should show the intended state
      // The cache invalidation will eventually sync the correct state
    }
  };

  // Load Default with caching to prevent excessive database calls
  useEffect(() => {
    // Only load default once per component lifecycle to prevent infinite loops
    if (hasLoadedDefaultRef.current) {
      return;
    }

    const getDefaultValue = async () => {
      // Create cache key for this specific observation
      const cacheKey = `${inspectionId}-${path}-${entityId}-${entityExternalId}`;

      // Check cache first
      if (observationCacheRef.current.has(cacheKey)) {
        const cachedValue = observationCacheRef.current.get(cacheKey);
        if (cachedValue) {
          setSelectedValue(cachedValue);
          // Don't call onDefaultLoad for cached values to prevent loops
          hasLoadedDefaultRef.current = true;
          return;
        }
      }

      try {
        const observation = await observationService.getObservation({
          dataLevel: dataLevel,
          entityId: entityId,
          entityExternalId: entityExternalId,
          inspectionId: inspectionId,
          inspectionCategoryId: inspectionCategoryTypeId,
          path: path,
        });

        if (observation) {
          const value = observationService.convertValue<string>(
            observation.value,
            DataTypesEnum.string,
          );

          // Cache the result
          observationCacheRef.current.set(cacheKey, value);

          if (
            path ===
              ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
                .ChangeOut.Specification.NewParts.Checked &&
            value === 'true'
          ) {
            setSelectedValue('YES');
            onDefaultLoad?.('YES');
          } else if (
            path ===
              ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
                .ChangeOut.Specification.SameParts.Checked &&
            value === 'true'
          ) {
            setSelectedValue('NO');
            onDefaultLoad?.('NO');
          } else {
            setSelectedValue(value);
            onDefaultLoad?.(value);
          }
        } else if (!observation && defaultValue) {
          setSelectedValue(defaultValue);
          onDefaultLoad?.(defaultValue);

          if (saveDefaultValueOnLoad === true) {
            await observationService.upsertObservation({
              dataLevel: dataLevel,
              entityId: entityId,
              entityExternalId: entityExternalId,
              inspectionCategoryId: inspectionCategoryTypeId,
              inspectionId: inspectionId,
              path: path,
              value: defaultValue,
            });

            // Clear the cache since we just saved a new default value
            await cacheInvalidationService.invalidateInspection(inspectionId);
          }
        } else if (alwaysRunOnDefaultLoad) {
          onDefaultLoad?.(defaultValue);
        }
      } catch (error) {
        console.error('Error loading default observation value:', error);
        // Fallback to default value on error
        if (defaultValue) {
          setSelectedValue(defaultValue);
          onDefaultLoad?.(defaultValue);
        }
      }

      hasLoadedDefaultRef.current = true;
    };

    void getDefaultValue();
  }, []); // Empty dependency array - only run once per component lifecycle

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between">
        {title && <Label>{title}</Label>}
        {tooltipKey && <Tooltip tooltipKey={tooltipKey} />}
      </div>
      {divider && (
        <div className="-mx-8">
          <hr className="h-0.5 bg-gray-200" />
        </div>
      )}

      <div className="space-y-3">
        {options.map((option) => (
          <label
            key={option.value}
            className="group flex cursor-pointer items-center py-2 hover:bg-brandLight/30"
          >
            <div className="relative">
              <input
                type="radio"
                name="visual-condition"
                value={option.value}
                checked={selectedValue === option.value}
                onChange={() => handleChange(option.value)}
                className="sr-only"
              />
              <div
                className={`size-5 rounded-full border-2 ${
                  selectedValue === option.value
                    ? 'border-brandOrange'
                    : 'border-line group-hover:border-line'
                }`}
              >
                {selectedValue === option.value && (
                  <div className="absolute top-1 left-1 size-3 rounded-full bg-brandOrange" />
                )}
              </div>
            </div>
            <span className="ml-3 text-base text-ink">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

interface VisualConditionProps {
  title?: string;
  onChange?: (value: string) => void;
  alwaysRunOnChange?: boolean;
  onDefaultLoad?: (value?: string) => void;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  divider?: boolean;
  tooltipKey?: string;
}

export const VisualCondition = (props: VisualConditionProps) => {
  return (
    <RadioButtonGroupField
      {...props}
      title={props.title ?? 'Visual Condition'}
      options={[
        { value: 'Good', label: 'Good' },
        { value: 'Monitor', label: 'Monitor' },
        { value: 'Changeout', label: 'Changeout' },
      ]}
      defaultValue="Good"
      saveDefaultValueOnLoad={true}
    />
  );
};
